/**
 * safeapp olive-green re-skin of the Safe Wallet UI MUI theme.
 *
 * Drop-in replacement for:
 *   apps/web/src/components/theme/safeTheme.ts
 * in safe-global/safe-wallet-monorepo (pinned ref: see theme/safe-ui-overlay/README.md).
 *
 * Strategy: keep the upstream theme generator (all component overrides, spacing,
 * radius, typography) and deep-merge the safeapp olive brand palette on top of it.
 * Semantic status colors (error / success / info / warning / review) are left
 * exactly as upstream ships them — only brand, neutral and surface colors change.
 *
 * COLOR SYNC CONTRACT: the hex values below are hardcoded on purpose (the Safe UI
 * is built in a separate cloned tree and cannot import files from this repo at
 * runtime). They MUST match theme/tokens.json in chews/safeapp. If tokens.json
 * changes, update this file in the same commit. The canonical mapping is
 * documented in theme/safe-ui-overlay/README.md.
 */
import type { PaletteMode, Theme } from '@mui/material'
// MUI type extensions (module augmentation for custom palette colors, button variants, etc.)
import '@safe-global/theme/generators/mui-extensions'
import { generateMuiTheme } from '@safe-global/theme/generators/mui'
import { createTheme } from '@mui/material/styles'
import type { Shadows } from '@mui/material/styles'

/**
 * safeapp olive design tokens (mirror of theme/tokens.json — per-mode ramps).
 * The ramp is semantic, not literal lightness (see theme/olive.css): `o600`
 * is the emphasized step (hover / pressed) — darker than base in light mode,
 * LIGHTER than base in dark mode; `o400` is the subdued step; `o200` the
 * tinted surface.
 *
 *                    light      dark
 *   --sa-olive       #5F6B2E    #98A65A   brand olive (base / primary action)
 *   --sa-olive-600   #49521F    #ACB96E   emphasis (hover / pressed)
 *   --sa-olive-400   #8C9A4B    #7C8A3F   subdued step
 *   --sa-olive-200   #DDE3C4    #2E3319   tinted surface
 *   on-olive         #FFFFFF    #14160F   text on olive (--sa-on-olive)
 */
const olive = {
  light: {
    base: '#5F6B2E',
    o600: '#49521F',
    o400: '#8C9A4B',
    o200: '#DDE3C4',
    onOlive: '#FFFFFF',
  },
  dark: {
    base: '#98A65A',
    o600: '#ACB96E',
    o400: '#7C8A3F',
    o200: '#2E3319',
    onOlive: '#14160F',
  },
} as const

/** Olive-cast neutrals (replace Safe's blue-grey neutral ramp 1:1). */
const neutral = {
  ink: '#1A1D14', // was #121312
  inkSoft: '#3D4234', // was #3C3C3C
  gray700: '#63665C', // was #636669
  gray600: '#6A6E60', // dark-mode secondary text / borders, was #636669
  gray400: '#A3A69B', // was #A1A3A7
  gray400d: '#A6A99C', // dark-mode primary.light, was #A1A3A7
  gray350: '#9A9E90', // light-mode secondary text, was #A1A3A7
  gray200: '#DDDFD6', // was #DCDEE0 / #DDDEE0
  gray100: '#F4F5F1', // was #F4F4F4
  logoBg: '#EEEFEA', // was #EEEFF0
} as const

/** Olive-cast surfaces. */
const surface = {
  tintLight: '#F3F6E7', // pale olive wash, was #F3F6E7
  chip: '#CBD79A', // pale-mid olive, was #CBD79A
  darkBg: '#14150F', // was #121312
  darkPaper: '#1D1E17', // was #1C1C1C
  darkTint: '#232619', // olive-tinted elevated surface, was #232619
  darkLine: '#30332B', // was #303033
} as const

/** Static brand colors (identical in light and dark, mirrors palettes/static.ts). */
const staticBrand = {
  main: neutral.ink,
  light: neutral.gray700,
  primary: '#FFFFFF',
  textSecondary: neutral.gray350,
  textBrand: olive.dark.base, // bright olive on dark/static surfaces
} as const

/**
 * Light-mode brand overrides. Structure mirrors the upstream ColorPalette:
 * in light mode, `primary` is the near-black ink and `secondary` is the brand color.
 * contrastText is set explicitly so no stale auto-computed value survives the merge.
 */
const lightBrandPalette = {
  text: {
    primary: neutral.ink,
    secondary: neutral.gray350,
    disabled: neutral.gray200,
    contrast: '#FFFFFF',
  },
  primary: {
    dark: neutral.inkSoft,
    main: neutral.ink,
    light: neutral.gray700,
    contrastText: '#FFFFFF',
  },
  secondary: {
    dark: olive.light.o600,
    main: olive.light.base,
    light: surface.chip,
    background: surface.tintLight,
    contrastText: olive.light.onOlive,
  },
  border: {
    main: neutral.gray400,
    light: neutral.gray200,
    background: neutral.gray100,
  },
  background: {
    default: neutral.gray100, // upstream generator hardcodes #F4F4F4 here; re-tint it
    main: neutral.gray100,
    sheet: neutral.gray100,
    paper: '#FFFFFF',
    light: surface.tintLight,
    secondary: neutral.gray200,
    lightGrey: neutral.gray200,
  },
  backdrop: { main: neutral.gray700 },
  logo: { main: neutral.ink, background: neutral.logoBg },
  static: staticBrand,
}

/**
 * Dark-mode brand overrides: `primary` is the brand color in dark mode.
 * Bright olive (#98A65A) keeps ~6.9:1 contrast (WCAG AA text) on the #14150F
 * background. `primary.dark` (MUI's contained-button hover shade) takes the
 * dark-mode --sa-olive-600, which is LIGHTER than base — mirroring
 * theme/olive.css, where the emphasis step raises contrast in dark mode.
 * contrastText is the tokens.json dark `onOlive` (near-black on bright olive).
 */
const darkBrandPalette = {
  text: {
    secondary: neutral.gray600,
  },
  primary: {
    dark: olive.dark.o600,
    main: olive.dark.base,
    light: neutral.gray400d,
    contrastText: olive.dark.onOlive,
  },
  secondary: {
    dark: neutral.gray600,
    main: '#FFFFFF',
    light: surface.chip,
    background: surface.darkTint,
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
  border: {
    main: neutral.gray600,
    light: surface.darkLine,
    background: surface.darkBg,
  },
  background: {
    default: surface.darkBg,
    main: surface.darkBg,
    sheet: surface.darkBg,
    paper: surface.darkPaper,
    light: surface.darkTint,
    secondary: surface.darkLine,
    lightGrey: surface.darkLine,
  },
  backdrop: { main: neutral.gray700 },
  logo: { main: '#FFFFFF', background: surface.darkLine },
  static: staticBrand,
}

/**
 * Rebuild the upstream shadow scale with the olive-cast shadow color
 * (upstream derives it from palette.primary.light, which we just changed).
 */
const buildShadows = (isDarkMode: boolean): Shadows => {
  const shadowColor = isDarkMode ? neutral.gray400d : neutral.gray700
  return [
    'none',
    isDarkMode ? `0 0 2px ${shadowColor}` : `0 1px 4px ${shadowColor}0a, 0 4px 10px ${shadowColor}14`,
    isDarkMode ? `0 0 2px ${shadowColor}` : `0 1px 4px ${shadowColor}0a, 0 4px 10px ${shadowColor}14`,
    isDarkMode ? `0 0 2px ${shadowColor}` : `0 2px 20px ${shadowColor}0a, 0 8px 32px ${shadowColor}14`,
    isDarkMode ? `0 0 2px ${shadowColor}` : `0 8px 32px ${shadowColor}0a, 0 24px 60px ${shadowColor}14`,
    ...(Array(20).fill('none') as string[]),
  ] as Shadows
}

/**
 * Create the safeapp-themed MUI theme for the given mode.
 * Uses the unified upstream theme package for structure, then re-brands it.
 */
const createSafeTheme = (mode: PaletteMode): Theme => {
  const isDarkMode = mode === 'dark'
  const base = generateMuiTheme(mode)

  // createTheme(theme, ...args) deep-merges args into the created theme.
  // Component styleOverrides in the upstream generator are lazy callbacks
  // (({ theme }) => ...), so they resolve against the merged olive palette.
  return createTheme(base, {
    palette: isDarkMode ? darkBrandPalette : lightBrandPalette,
    shadows: buildShadows(isDarkMode),
  })
}

export default createSafeTheme
